import { useQueries } from '@tanstack/react-query';
import { hasAnyRole } from '@/shared/guards/roles';
import { useEffectiveRoles } from '@/shared/view-as/use-effective-roles';
import { PENDING_SOURCES } from '../../registry/sources';
import type { PendingSource } from '../../domain/entities/pending-alert.entity';

/**
 * "Existe pelo menos um alerta aberto que eu possa ver?" — o sinal da sidebar.
 *
 * Cada fonte responde com uma página de **tamanho 1** do próprio `fetch` e o
 * `total` dela: o contrato de `PendingSource` não precisa de método novo, e o
 * boot do app não dispara as listagens de 20 da tela.
 *
 * O gate por papel é o mesmo do `usePendingAlerts`: fonte sem papel nunca vai
 * à rede. Fonte que falhou não acende o sinal — a tela é que diz que deu erro.
 *
 * A chave é a mesma forma da tela (`['pending-alerts', key, perPage]`), então o
 * `invalidateQueries(['pending-alerts'])` de quem fecha um alerta apaga o sinal
 * junto quando a fila esvazia. Revalida no foco da aba (default do React Query).
 */
export function useHasPendingAlerts(
  sources: PendingSource[] = PENDING_SOURCES,
): boolean {
  const roles = useEffectiveRoles();
  const allowed = sources.filter((source) =>
    hasAnyRole(roles, source.requiredRoles),
  );
  const queries = useQueries({
    queries: allowed.map((source) => ({
      queryKey: ['pending-alerts', source.key, 1],
      queryFn: () => source.fetch({ page: 1, perPage: 1 }),
      staleTime: 1000 * 30,
    })),
  });
  return queries.some((query) => (query.data?.total ?? 0) > 0);
}
